import { Controller } from "@hotwired/stimulus";

// Per-section ↻ Oscar rewrite in the page builder. Attached to a block
// card (or the hero subsection of the page-settings card, with a
// "hero." path prefix). Collects the section's current copy from its
// inputs, posts to the regenerate endpoint, and writes the returned
// fields back into the inputs — dispatching "input" so the editor's
// auto-save (draft) treats it like a manual edit.
//
// Endpoint URL / page slug / CSRF ride the page-editor root's
// data-oscar-regenerate-url / -page / -csrf attributes (shared with the
// field-level ✨ refine).
export default class extends Controller {
  static targets = ["button"];
  static values = {
    type: String,
    prefix: { type: String, default: "" },
  };

  async regenerate() {
    const root = this.element.closest("[data-oscar-refine-url]");
    if (!root || this.busy) return;

    this.setBusy(true);
    try {
      const response = await fetch(root.dataset.oscarRegenerateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": root.dataset.oscarRefineCsrf,
          Accept: "application/json",
        },
        body: JSON.stringify({
          block_type: this.typeValue,
          settings: this.collectSettings(),
          page_slug: root.dataset.oscarRefinePage,
        }),
      });
      const data = await response.json();
      if (response.ok && data.settings) {
        this.applySettings(data.settings);
      } else {
        this.flashError(data.error || "Oscar couldn't rewrite that section — try again.");
      }
    } catch {
      this.flashError("Network hiccup — try again.");
    } finally {
      this.setBusy(false);
    }
  }

  // Current copy values, keyed by path with the prefix stripped
  // ("hero.headline" → "headline") so the server sees plain field names.
  collectSettings() {
    const settings = {};
    this.inputs().forEach((input) => {
      const path = this.localPath(input);
      if (!path) return;
      const type = input.dataset.settingType || "string";
      if (type === "array_of_strings") {
        settings[path] = (input.value || "").split("\n").map((s) => s.trim()).filter(Boolean);
      } else if (type === "string") {
        settings[path] = input.value;
      }
    });
    return settings;
  }

  applySettings(settings) {
    Object.entries(this.flatten(settings)).forEach(([path, value]) => {
      const input = this.inputs().find((candidate) => this.localPath(candidate) === path);
      if (!input) return;
      input.value = Array.isArray(value) ? value.join("\n") : value;
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });
  }

  // {"cards": [{"title": "A"}]} → {"cards.0.title": "A"}; arrays of
  // strings stay leaves (the bullets textarea joins them itself).
  flatten(obj, prefix = "") {
    const out = {};
    Object.entries(obj).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      const isStringArray = Array.isArray(value) && value.every((v) => typeof v !== "object");
      if (value !== null && typeof value === "object" && !isStringArray) {
        Object.assign(out, this.flatten(value, path));
      } else {
        out[path] = value;
      }
    });
    return out;
  }

  inputs() {
    return Array.from(this.element.querySelectorAll("[data-setting-path]"));
  }

  localPath(input) {
    const path = input.dataset.settingPath || "";
    if (!this.prefixValue) return path;
    return path.startsWith(this.prefixValue) ? path.slice(this.prefixValue.length) : null;
  }

  setBusy(busy) {
    this.busy = busy;
    if (!this.hasButtonTarget) return;
    this.buttonTarget.disabled = busy;
    this.buttonTarget.classList.toggle("animate-pulse", busy);
  }

  flashError(message) {
    if (!this.hasButtonTarget) return;
    const button = this.buttonTarget;
    const original = button.title;
    button.title = message;
    button.classList.add("text-red-500");
    setTimeout(() => {
      button.title = original;
      button.classList.remove("text-red-500");
    }, 4000);
  }
}
